import {
  GridColumns as XGridColumns,
  GridColumnVisibilityModel,
  GridPinnedColumns,
  GridSortModel,
  useGridApiRef,
  GridRowClassNameParams,
} from '@mui/x-data-grid-pro';
import { XGrid } from '@AMIEWEB/Common';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultPinnedColumns, orderPayPackageColumns, orderPayPackagesColumnVisibility } from './utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConfirmationDialog,
  selectDirtyRows,
  selectIsMarginOrderBaseDetailsValid,
  selectOrderPayPackages,
  selectOrderPayPackagesGridActiveEditRow,
  selectOrderPayPackagesGridIsDirty,
  selectOrderPayPackagesInteraction,
  selectTabValue,
} from '@AMIEWEB/Order/Store/Order.selectors';
import { transformOrderPayPackages } from '@AMIEWEB/Order/Store/helpers/sagaHelper';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { usePromiseTracker } from 'react-promise-tracker';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserGridPreference, selectUserPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import _ from 'lodash';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { formatMultiSortColumns, formatMultiSortXGridColumns } from '@AMIEWEB/GlobalSearch/helper';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { selectGridSelection } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.selector';
import { OrderPayPackagesAction } from '@AMIEWEB/Order/Store/types';
import { ConfirmationDialog } from '@AMIEWEB/Settings/AutoTaskManagement/common/ConfirmationDialog';
import { navigationStickActions } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.redux';
import { ordersMenu } from '../../OrderPreferences/SubmissionPreferences/SubmissionPreferences';
import { useConfirmExit } from 'utils/customHooks/useConfirmExit';
import { useCleanup } from 'utils/customHooks/useCleanup';
import { makeStyles } from 'tss-react/mui';
import { useReadOnly } from 'oidc/userRoles';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
const useStyles = makeStyles()(themes => ({
  highlightRow: {
    backgroundColor: "rgba(0, 111, 185, 0.08)" + ' !important',
  },
}));
const OrderPayPackageTab = () => {
  const gridTag = 'order-pay-packages-grid';

  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: orderId } = useParams();
  const apiRef = useGridApiRef();
  const { promiseInProgress: getOrderPayPackagesInProgress } = usePromiseTracker({ area: 'get-Order-Pay-Packages' });
  const { promiseInProgress: getPeoplesoftCalculatedValueInProgress } = usePromiseTracker({
    area: 'track-get-peopleSoft-calculated-values',
  });
  const { promiseInProgress: isSaving } = usePromiseTracker({ area: 'save-Order-Pay-Packages' });
  const { promiseInProgress: orderPayPackageResetInProgress } = usePromiseTracker({
    area: 'track-get-order-packages-reset-values',
  });
  const orderPayPackages = useSelector(selectOrderPayPackages);
  const gridPreferences = useSelector(selectUserGridPreference).find(item => item.id === gridTag);
  const gridState = useSelector(selectGridState);
  const dirtyRows = useSelector(selectDirtyRows);
  const userPreference = useSelector(selectUserPreference);
  const orderPayPackagesGridIsDirty = useSelector(selectOrderPayPackagesGridIsDirty);
  const orderPayPackagesGridActiveEditRow = useSelector(selectOrderPayPackagesGridActiveEditRow);
  const { selectedData } = useSelector(selectGridSelection);
  const orderPayPackagesInteraction = useSelector(selectOrderPayPackagesInteraction);
  const confirmationDialog = useSelector(selectConfirmationDialog);
  const tabValue = useSelector(selectTabValue);
  const { readOnly } = useReadOnly();
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);
  const isMarginBaseValidFromState = useSelector(selectIsMarginOrderBaseDetailsValid);
  const isOrderMarginBaseDetailsValid =
    orderDetailsUIView?.orderDetails?.isOrderMarginBaseDetailsIsValid === true || isMarginBaseValidFromState === true;

  const [columns, setColumns] = useState(orderPayPackageColumns(t));
  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumns>(defaultPinnedColumns);
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(orderPayPackagesColumnVisibility),
  );
  const [triggerExit, setTriggerExit] = useState({
    isExit: false,
    path: '',
  });
  const { blockExit, allowExit } = useConfirmExit();
  const history = useNavigate();
  useCleanup(() => {
    dispatch(orderDataActions.orderPackagesDiscardChanges());
  });
  //**Effects START*/
  useEffect(() => {
    if (gridPreferences?.value?.multiSortedColumns?.length > 0) {
      setCurrentSortModel(getSortModelPreferences(gridPreferences?.value?.multiSortedColumns));
    }
  }, [gridPreferences]);
  useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(gridTag));
      determineVisibilityModel();
      determineColumn();
      determinePinnedColumns();
    }
  }, [userPreference?.loaded, gridPreferences]);
  useEffect(() => {
    if (orderPayPackagesGridIsDirty) {
      blockExit(() => {
        if (triggerExit.path !== '' && triggerExit.path !== location.pathname) {
          setTriggerExit(obj => ({ ...obj, path: location.pathname }));
          dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: true }));
        }
      });
    } else {
      allowExit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPayPackagesGridIsDirty, location.pathname, blockExit, allowExit]);
  const shouldBlock = useCallback(
    ({ nextLocation }) => {
      if (nextLocation.pathname !== location.pathname && orderPayPackagesGridIsDirty) {
        return true;
      }
      return false;
    },
    [location.pathname, orderPayPackagesGridIsDirty],
  );

  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (orderPayPackagesGridIsDirty && blocker.state === 'blocked') {
      dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: true }));
    }
  }, [blocker, orderPayPackagesGridIsDirty]);
  //Navigation Helpers
  const handleGoToIntendedLocation = useCallback(
    location => {
      history(`${location}${tabValue !== 6 ? `?tab=${tabValue}` : ''}`);
    },
    [history, tabValue],
  );

  useEffect(() => {
    if (triggerExit.isExit && triggerExit.path) {
      handleGoToIntendedLocation(triggerExit.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGoToIntendedLocation, triggerExit.isExit, triggerExit.path]);

  //**Effects END*/
  const getSortModelPreferences = (sortModel: any[]) => {
    const columnPreferences = gridPreferences?.value;
    if (
      columnPreferences &&
      columnPreferences?.multiSortedColumns &&
      Array.isArray(columnPreferences?.multiSortedColumns)
    ) {
      return formatMultiSortXGridColumns(columnPreferences?.multiSortedColumns);
    }
    return sortModel;
  };
  const rows = useMemo(() => (orderPayPackages ? transformOrderPayPackages(orderPayPackages) : []), [orderPayPackages]);
  const fetchOrderPayPackages = () => {
    dispatch(orderDataActions.orderPackagesDiscardChanges());
    dispatch(orderDataActions.getOrderPayPackages({ orderId: Number(orderId) }));
  };
  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      setPinnedColumns(updatedPinnedColumns);
      const preferenceData = {
        id: gridTag,
        value: {
          ...(gridPreferences?.value || {}),
          pinnedColumns: updatedPinnedColumns,
        },
      };
      dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    },
    [dispatch, gridTag, gridPreferences?.value, setPinnedColumns],
  );

  //***RESET COLUMNS */
  const resetColumns = () => {
    //Reset grid attributes to default values
    const defaultColumns = orderPayPackageColumns(t);
    setColumns(defaultColumns);
    setCurrentSortModel([]);
    setCurrentColumnVisibilityModel(orderPayPackagesColumnVisibility);
    setPinnedColumns(defaultPinnedColumns);

    const preferenceData = {
      id: gridTag,
      value: {
        ...(gridPreferences?.value || {}),
        columns: defaultColumns,
        columnVisibilityModel: orderPayPackagesColumnVisibility,
        pinnedColumns: defaultPinnedColumns,
        multiSortedColumns: [],
      },
    };
    dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
    dispatch(
      globalActions.setSnackBar({
        message: 'Columns have been reset.',
        severity: 'success',
      }),
    );
  };

  //**SORT MODEL CHANGE */
  const onSortModelChanged = (model: GridSortModel) => {
    saveSortModel(model);
    setCurrentSortModel(model);
  };
  const saveSortModel = React.useCallback(
    sortModel => {
      const multiSortedColumns = sortModel?.length > 0 ? formatMultiSortColumns(sortModel) : undefined;
      if (!_.isEqual(multiSortedColumns, gridState?.multiSortedColumns)) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            multiSortedColumns: multiSortedColumns,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridPreferences?.value, columns?.length, gridState?.multiSortedColumns, currentColumnVisibilityModel],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      field?: string;
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!columns || columns.length === 0 || !userPreference.loaded) return;
      const currentGridColumns = new GridColumns(gridColumns).columns;

      // Remove the automatic checkbox column (has no field name)
      if (currentGridColumns && currentGridColumns.length > 0 && !currentGridColumns[0].field)
        currentGridColumns.shift();

      const comparableColumns = new GridColumns(
        gridPreferences?.value?.columns,
        gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        const preferenceData = {
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };
        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    [userPreference.loaded, gridPreferences?.value, dispatch],
  );
  const getVisibilityPreferences = (cols: any) => {
    const columnPreferences = gridPreferences?.value;
    if (columnPreferences && columnPreferences?.columnVisibilityModel) {
      return columnPreferences?.columnVisibilityModel;
    }
    return cols;
  };

  const determineVisibilityModel = () => {
    const currentVisibilityModel = getVisibilityPreferences(orderPayPackagesColumnVisibility);
    setCurrentColumnVisibilityModel(currentVisibilityModel);
  };
  const determinePinnedColumns = () => {
    const pinnedColumn = gridPreferences?.value?.pinnedColumns || defaultPinnedColumns;
    setPinnedColumns(pinnedColumn);
  };
  const determineColumn = () => {
    const currentColumn = getColumnPreferences(columns);
    setColumns(currentColumn);
  };
  //Get the grid columns from preferences if available
  const getColumnPreferences = (cols: any[]) => {
    try {
      const columnPreferences = gridPreferences?.value;
      const filterSetColumns = gridPreferences?.value?.filterAttributes?.filterSet?.find(
        item => item.name === gridPreferences?.value?.selectedFilter?.name,
      )?.columns;
      if (columnPreferences && (columnPreferences.columns || filterSetColumns)) {
        const newColumns = cols.map(column => {
          const foundIndex = (filterSetColumns ?? columnPreferences.columns).findIndex(
            item => item?.field === column?.field,
          );
          if (foundIndex !== -1) {
            return {
              ...column,
              ...(filterSetColumns ?? columnPreferences.columns)[foundIndex],
            };
          } else {
            return column;
          }
        });
        return newColumns.sort((a, b) => {
          const filterSetColumnsIndexA = (filterSetColumns ?? columnPreferences.columns).findIndex(
            i => i.field === a.field,
          );
          const filterSetColumnsIndexB = (filterSetColumns ?? columnPreferences.columns).findIndex(
            i => i.field === b.field,
          );

          const indexA = filterSetColumnsIndexA === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexA;
          const indexB = filterSetColumnsIndexB === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexB;

          return indexA - indexB;
        });
      }
      return cols;
    } catch (err) {
      console.error('Error in getColumnPreferences:', err);
      return cols;
    }
  };
  const leftActions = React.useMemo<IXGridBulkActionButton[]>(
    () => [
      // @ts-ignore
      {
        title: t('candidate.workExperience.workExperienceGrid.saveChanges'),
        Icon: () => (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckIcon />
            {isSaving && <CircularProgress size={14} />}
          </Stack>
        ),
        disabled:
          !orderPayPackagesGridIsDirty ||
          isSaving ||
          getPeoplesoftCalculatedValueInProgress ||
          orderPayPackageResetInProgress || 
          readOnly,
        onClick: () => {
          const updatedRows = rows.filter(
            row => row.mode === OrderPayPackagesAction.UPDATE_MARGIN || row.mode === OrderPayPackagesAction.RESET,
          );
          dispatch(orderDataActions.saveOrderPaypackages(updatedRows));
        },
      },
      // @ts-ignore
      {
        title: t('candidate.workExperience.workExperienceGrid.cancelChanges'),
        Icon: ClearIcon,
        disabled:
          !orderPayPackagesGridIsDirty ||
          isSaving ||
          getPeoplesoftCalculatedValueInProgress ||
          orderPayPackageResetInProgress,
        onClick: () => {
          dispatch(orderDataActions.orderPackagesDiscardChanges());
          apiRef?.current?.setSelectionModel([]); //uncheck selected rows
        },
      },
      // @ts-ignore
      {
        title: t('order.payPackagesTab.orderPayPackages.grid.button.resetPayPackages'),
        Icon: () => (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReplayRoundedIcon />
            {orderPayPackageResetInProgress && <CircularProgress size={14} />}
          </Stack>
        ),
        disabled:
          selectedData.length === 0 ||
          selectedData.length > 5 ||
          isSaving ||
          orderPayPackageResetInProgress ||
          orderPayPackagesInteraction.action === OrderPayPackagesAction.UPDATE_MARGIN
          || readOnly,
        onClick: () => {
          const selectedRows = rows
            .filter(row => selectedData.includes(row.scenarioId))
            .map(row => ({ scenarioId: row.scenarioId, orderId: row.orderId, bookingPeriodId: row.bookingPeriodId }));
          dispatch(orderDataActions.getOrderPackagesResetValues(selectedRows));
          dispatch(orderDataActions.setOrderPayPackagesInteraction({ action: OrderPayPackagesAction.RESET }));
        },
        tooltipProps: {
          tooltipContent: t('order.payPackagesTab.orderPayPackages.grid.button.resetPayPackageTooltipsMessage'),
          disabled: selectedData.length <= 5 && selectedData.length > 0,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orderPayPackagesGridIsDirty,
      isSaving,
      rows,
      getPeoplesoftCalculatedValueInProgress,
      orderPayPackageResetInProgress,
      selectedData,
      orderPayPackagesInteraction,
      readOnly
    ],
  );

  const processRowUpdate = useCallback((newRow, oldRow) => {
    if (newRow.currentTargetMargin !== oldRow.currentTargetMargin) {
      //call people soft api to update the record
      dispatch(orderDataActions.getPeopleSoftCalculatedValues(newRow));
    }
    return newRow;
  }, []);

  // Disable editing for other rows while saving
  const isCellEditable = React.useCallback(
    (params: any) => {
      if (
        getPeoplesoftCalculatedValueInProgress ||
        getOrderPayPackagesInProgress ||
        isSaving ||
        orderPayPackageResetInProgress ||
        orderPayPackagesInteraction.action === OrderPayPackagesAction.RESET ||
        readOnly
      )
        return false;
      return true;
    },
    [
      getPeoplesoftCalculatedValueInProgress,
      getOrderPayPackagesInProgress,
      orderPayPackagesGridActiveEditRow,
      orderPayPackagesInteraction,
      isSaving,
      orderPayPackageResetInProgress,
      readOnly,
    ],
  );
  const onCellEditStart = React.useCallback((params, event) => {
    dispatch(orderDataActions.setOrderPayPackagesGridActiveEditRow(params?.row));
  }, []);
  //handling modal action items:
  const handleKeepChanges = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }

    dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: false }));
    setTriggerExit(obj => ({
      ...obj,
      path: location.pathname,
      isExit: false,
    }));
    dispatch(navigationStickActions.setSelectedMenu(ordersMenu));
  };
  const discardChanges = () => {
    dispatch(orderDataActions.setConfirmationDialog({ ...confirmationDialog, open: false }));
    dispatch(orderDataActions.setIsPreferenceChaged(false));
    dispatch(orderDataActions.setTabValue(confirmationDialog.tab));
    setTriggerExit(obj => ({
      ...obj,
      isExit: true,
      path: location.pathname,
    }));
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  if (!isOrderMarginBaseDetailsValid) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography>{`Target margins are not defined for this order's division.`}</Typography>
      </Box>
    );
  }

  return (
    <LayoutGrid>
      <LayoutGridItem xs={12}>
        <XGrid
          getRowId={row => row.scenarioId}
          gridTag={gridTag}
          columns={columns}
          apiRef={apiRef}
          rows={rows}
          editMode="cell"
          autoHeight
          checkboxSelection
          disableSelectionOnClick
          loading={getOrderPayPackagesInProgress}
          pinnedColumns={pinnedColumns}
          onPinnedColumnsChange={handlePinnedColumnsChange}
          defaultSortModel={currentSortModel}
          sortModel={currentSortModel}
          onSortModelChange={onSortModelChanged}
          defaultColumnVisibilityModel={orderPayPackagesColumnVisibility}
          disableMultipleColumnsSorting={false}
          experimentalFeatures={{ newEditingApi: true }}
          processRowUpdate={processRowUpdate}
          isCellEditable={isCellEditable}
          onCellEditStart={onCellEditStart}
          columnVisibilityModel={currentColumnVisibilityModel}
          onColumnVisibilityModelChange={(model, details) => {
            dispatch(gridStateActions.setColumnVisibilityModel(model));
            setCurrentColumnVisibilityModel(_.cloneDeep(model));
            savePreferences({
              field: undefined,
              columns: _.cloneDeep(apiRef?.current?.getAllColumns()),
              visibilityModel: _.cloneDeep(model),
            });
          }}
          onColumnOrderChange={(params, event, details, _columns) => {
            savePreferences({
              field: params.field,
              columns: _columns,
              visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? orderPayPackagesColumnVisibility),
            });
          }}
          onColumnResizeStop={_columns => {
            savePreferences({
              field: undefined,
              columns: _columns,
              visibilityModel: _.cloneDeep(currentColumnVisibilityModel ?? orderPayPackagesColumnVisibility),
            });
          }}
          toolbarProps={{
            refreshButton: true,
            refreshGrid: () => fetchOrderPayPackages(),
            resetButton: true,
            resetColumns: resetColumns,
            leftActions: leftActions,
          }}
          getRowClassName={(params: GridRowClassNameParams) =>
            dirtyRows.includes(params.id as number) ? classes.highlightRow : ''
          }
        />
        {/* Confirmation Dialog */}
        <ConfirmationDialog
          dialogeOpen={confirmationDialog.open}
          keepChanges={handleKeepChanges}
          handleDiscard={discardChanges}
        />
      </LayoutGridItem>
    </LayoutGrid>
  );
};

export default OrderPayPackageTab;
